import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { PlatformSessionInfo } from "@hik/contracts/platform";
import { ApiError, get, post } from "../../lib/api";
import { pk } from "./api";

interface PlatformCtx {
  session: PlatformSessionInfo | null;
  loading: boolean;
  refresh: () => Promise<unknown>;
  logout: () => Promise<void>;
}

const Ctx = createContext<PlatformCtx | null>(null);

/** Platform yöneticisi oturumu (firma oturumundan bağımsız; ayrı çerez, ayrı önbellek anahtarı). */
export function PlatformProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: pk.session,
    queryFn: async () => {
      try {
        return await get<PlatformSessionInfo>("/platform/auth/session");
      } catch (e) {
        if (e instanceof ApiError && (e.status === 401 || e.status === 404)) return null;
        throw e;
      }
    },
    staleTime: 30_000,
    retry: false,
  });

  useEffect(() => {
    const onUnauth = () => qc.setQueryData(pk.session, null);
    window.addEventListener("hik:platform-unauthenticated", onUnauth);
    return () => window.removeEventListener("hik:platform-unauthenticated", onUnauth);
  }, [qc]);

  // Panel sayfaları arama motorlarına kapalı.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  const value: PlatformCtx = {
    session: q.data ?? null,
    loading: q.isPending,
    refresh: () => q.refetch(),
    logout: async () => {
      try {
        await post("/platform/auth/logout");
      } finally {
        // Önce oturum boşaltılır (bu sorguyu izleyen sağlayıcı hemen yeniden çizilir), sonra diğer panel önbelleği
        // silinir. Oturum sorgusu da silinirse sağlayıcı eski sorguya bağlı kalıp eski oturumu göstermeye devam
        // ediyordu: çıkış sunucuda yapılmış ama panel açık kalıyor, giriş sayfası panele geri yönlendiriyordu.
        qc.setQueryData(pk.session, null);
        qc.removeQueries({ queryKey: ["platform"], predicate: (q) => q.queryKey[1] !== pk.session[1] });
      }
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePlatform(): PlatformCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("PlatformProvider missing");
  return v;
}

export function usePlatformAuthed() {
  const v = usePlatform();
  if (!v.session) throw new Error("platform session required");
  return { ...v, session: v.session };
}
